import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { ToastrService } from "../../../../../../services/toastr.service";
import { BranchesService } from "../../../../../../services/branches-service";
import { IDiagnosisValue } from "../../../../../../resources-vue/vue-interfaces/i-diagnosis";
import { IBranchResponse } from "../../../../../../resources-vue/vue-interfaces/i-branch";
import { getHospiceRedirectionUrl } from "../../../../../../common/get-hospice-redirection-url";

import TableEmpty from "../../../../../../resources-vue/vue-custom-elements/TableEmpty/TableEmpty.vue";
import HospiceDiagnosisTableRow from "./HospiceDiagnosisTableRow.vue";

export default defineComponent({
    components: {
        TableEmpty,
        HospiceDiagnosisTableRow
    },
    props: {
        diagnosis: {
            type: Array<IDiagnosisValue>
        },
        id: {
            type: String
        },
        snapshotView: {
            type: Boolean,
            default: false
        },
        updateDiagnosis: {
            type: Function
        },
        patientDetails: {
            type: Object
        },
        patientId: {
            type: String
        },
        accountId: {
            type: String
        }
    },
    data(vm) {
        return {
            _branchesService: null,
            newDiagnosis: [] as IDiagnosisValue[],
            _toastrService: null,
            isNewTerminal: false as boolean,
            columns: 5 as number,
            isAddRequested: true as boolean
        };
    },
    created() {
        this._toastrService = Container.instance.get(ToastrService);
        this._branchesService = Container.instance.get(BranchesService);
    },
    mounted() {
        if (this.diagnosis.length) {
            const temp = [
                ...this.diagnosis
                    .filter((d: IDiagnosisValue) => !d.resolvedDate)
                    .sort((a: any, b: any) => a.order - b.order),
                ...this.diagnosis.filter((d: IDiagnosisValue) => d.resolvedDate)
            ];
            this.updateDiagnosis(temp);
        }
        if (this.diagnosis && this.diagnosis[0] && this.diagnosis[0].order != 1) {
            const temp = Array(this.diagnosis[0].order - 1)
                .fill(null)
                .concat(this.diagnosis);
            this.updateDiagnosis(temp);
        }
    },
    computed: {
        activeDiagnosis() {
            if (this.diagnosis.length > 0) {
                return !this.snapshotView
                    ? this.diagnosis
                          .filter((dia: IDiagnosisValue) => !dia.resolvedDate)
                          .map((diag: IDiagnosisValue, index) => {
                              diag.order = index + 1;
                              if (diag.order === 1) {
                                  diag.isRelated = null;
                                  diag.notRelatedComments = null;
                              }
                              return diag;
                          })
                    : this.diagnosis;
            }
            return [];
        }
    },
    methods: {
        addDiagnosis() {
            if (this.diagnosis.length == 0) {
                this.isNewTerminal = true;
            } else {
                this.isNewTerminal = false;
            }
            this.newDiagnosis.push({
                code: "",
                description: "",
                order: null,
                isRelated: null,
                notRelatedComments: null,
                startDate: null,
                resolvedDate: null,
                billable: null
            });
        },
        saveNewDiagnosis(index: number) {
            let arr = [...this.diagnosis];
            this.newDiagnosis[index].order = this.activeDiagnosis.length + 1;
            arr.push(this.newDiagnosis[index]);
            this.newDiagnosis.splice(index, 1);
            let temp = [
                ...arr.filter((d: IDiagnosisValue) => !d.resolvedDate),
                ...arr.filter((d: IDiagnosisValue) => d.resolvedDate)
            ];
            this.updateDiagnosis(temp);
        },
        cancelSaveNewDiagnosis(index: number) {
            this.newDiagnosis.splice(index, 1);
        },
        makePrimary(index: number) {
            let arr = [...this.diagnosis];
            let newPrimary: IDiagnosisValue = arr[index];
            newPrimary.isRelated = null;
            newPrimary.notRelatedComments = null;
            let oldPrimary = arr[0];
            arr[0] = newPrimary;
            arr[index] = oldPrimary;
            arr = arr.map((diagnosis: IDiagnosisValue, i) => {
                if (!diagnosis.resolvedDate) {
                    diagnosis.order = i + 1;
                }
                return diagnosis;
            });
            let temp = arr;
            this.updateDiagnosis(temp);
            this.showDiagnosisMessage();
        },
        moveDiagnosis(oldIndex: number, newIndex: number) {
            let arr = this.diagnosis;
            if (oldIndex >= arr.length || oldIndex < 0) {
                return;
            }
            if (newIndex >= arr.length || newIndex < 0) {
                return;
            }
            arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
            arr = arr.map((diagnosis: IDiagnosisValue, index) => {
                if (!diagnosis.resolvedDate) {
                    diagnosis.order = index + 1;
                }
                return diagnosis;
            });
            let temp = arr;
            this.updateDiagnosis(temp);
            if (oldIndex === 0 || newIndex === 0) {
                this.showDiagnosisMessage();
            }
        },
        resolveDiagnosis(index: number) {
            const limit = 1;
            if (this.diagnosis.length > limit && index < this.diagnosis.length) {
                let orderCount = 1;
                let temp = this.diagnosis.map((diagnosis: IDiagnosisValue) => {
                    if (!diagnosis.resolvedDate) {
                        diagnosis.order = orderCount;
                        orderCount++;
                    }
                    return diagnosis;
                });
                this.updateDiagnosis(temp);
            }
        },
        deleteDiagnosis(index: number) {
            this.diagnosis.splice(index, 1);
            let temp = this.diagnosis.map((diagnosis: IDiagnosisValue, diagnosisIndex) => {
                if (!diagnosis.resolvedDate) {
                    diagnosis.order = diagnosisIndex + 1;
                }
                return diagnosis;
            });
            this.updateDiagnosis(temp);
        },
        showDiagnosisMessage() {
            if (this.diagnosis.length > 1) {
                this._toastrService.info({
                    title: "New Terminal Diagnosis",
                    message:
                        "You have added a new Terminal Diagnosis. Please review all Comorbidities and update their relatedness status based on the new Terminal Diagnosis."
                });
            }
        },
        updateDiag(value: any, index: number) {
            let temp = [...this.newDiagnosis];
            temp = temp.map((el, idx) => {
                if (idx === index) {
                    return value;
                } else {
                    return el;
                }
            });
            this.newDiagnosis = temp;
        },
        async redirectToDiagnosisHistory() {
            const patientBranchId = this.patientDetails.locationId;
            const branches: Array<IBranchResponse> = await this._branchesService.getAllBranches();
            const patientBranch = branches.find((branch: IBranchResponse) => branch.id === patientBranchId);
            window.open(
                `${getHospiceRedirectionUrl(String(this.accountId))}/patients/diagnosis-history/${this.patientId}?branchIds%5B%5D=${patientBranch.id}&status=${this.patientDetails.status}?accountId=${this.accountId}`
            );
        }
    }
});
